import React, { useState, useEffect, useRef, useMemo } from 'react';
import QRCode from 'react-qr-code';
import { isManualPaymentMethod } from '@/lib/paymentMethodUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeft,
    Lock,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Zap,
    Shield,
    CheckCircle2,
    CreditCard,
} from 'lucide-react';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import {
    useCreateTradingAccountDeposit,
    useCreateTradingAccountWithdrawal,
    usePaymentMethods,
} from '@/api/transactions/use-trading-account-transactions';
import { useSnackbar } from 'notistack';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import { formatCurrencyAmount } from '@/utils/formatters';
import { Loader2 } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';

const MobileDepositWithdrawPage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action') || 'deposit';
    const { enqueueSnackbar } = useSnackbar();
    const hasInitialized = useRef(false);
    const { profile } = useProfile();

    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [selectedMethod, setSelectedMethod] = useState('');
    const [amount, setAmount] = useState('');
    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showPaymentMethodDropdown, setShowPaymentMethodDropdown] = useState(false);
    const [processorResponse, setProcessorResponse] = useState<any>(null);
    const [manualConfigId, setManualConfigId] = useState('');
    const [manualLoading, setManualLoading] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
        address: '',
        holder_name: '',
        iban: '',
        card_number: '',
        card_holder: '',
        expiry_date: '',
        cvv: '',
        customer_first_name: '',
        customer_last_name: '',
        customer_email: '',
        customer_dob: '',
        customer_address: '',
        customer_city: '',
        customer_country: '',
        customer_zip_code: '',
    });

    const { data: tradingAccounts, isLoading: isLoadingAccounts } = useTradingAccounts();
    const { data: paymentMethods = [] } = usePaymentMethods();
    const createDeposit = useCreateTradingAccountDeposit();
    const createWithdrawal = useCreateTradingAccountWithdrawal();

    const isDeposit = action === 'deposit';

    // Set default account when accounts load (only once)
    useEffect(() => {
        if (
            !hasInitialized.current &&
            tradingAccounts?.accounts &&
            tradingAccounts.accounts.length > 0
        ) {
            const firstAccountId = tradingAccounts.accounts[0].id;
            setSelectedAccountId(firstAccountId);
            hasInitialized.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tradingAccounts?.accounts?.length]);

    const selectedAccount = React.useMemo(() => {
        return tradingAccounts?.accounts?.find(acc => acc.id === selectedAccountId);
    }, [tradingAccounts?.accounts, selectedAccountId]);

    // Filter payment methods by currency - check if currency is in supported_currencies array
    const availablePaymentMethods = React.useMemo(() => {
        if (!selectedAccount?.base_currency || !paymentMethods.length) {
            return [];
        }
        return paymentMethods.filter(pm => {
            // Check if payment method supports the selected account's currency
            return (
                pm.supported_currency_symbols?.includes(selectedAccount.base_currency) ||
                pm.supported_currencies?.some(
                    (c: any) =>
                        (typeof c === 'string' && c === selectedAccount.base_currency) ||
                        (typeof c === 'object' && c?.symbol === selectedAccount.base_currency)
                )
            );
        });
    }, [paymentMethods, selectedAccount?.base_currency]);

    const selectedPaymentMethod = React.useMemo(() => {
        return availablePaymentMethods.find(pm => pm.id === selectedMethod);
    }, [availablePaymentMethods, selectedMethod]);
    const paymeshFieldLabels: Record<string, string> = {
        customer_first_name: 'First name',
        customer_last_name: 'Last name',
        customer_email: 'Email',
        customer_dob: 'Date of birth',
        customer_address: 'Address',
        customer_city: 'City',
        customer_country: 'Country',
        customer_zip_code: 'ZIP code',
    };
    const requiresPayMeshCustomerFields = useMemo(() => {
        if (!isDeposit || !selectedPaymentMethod || isManualPaymentMethod(selectedPaymentMethod)) {
            return false;
        }
        if (selectedPaymentMethod.supports_processors) {
            // Fallback: processor-backed methods may resolve to PayMesh at runtime.
            return true;
        }
        const haystack = [
            selectedPaymentMethod.name,
            selectedPaymentMethod.code,
            selectedPaymentMethod.display_name,
            selectedPaymentMethod.description,
            JSON.stringify(selectedPaymentMethod.processor_config || {}),
            JSON.stringify(selectedPaymentMethod.metadata || {}),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return haystack.includes('paymesh') || haystack.includes('pay-mesh');
    }, [isDeposit, selectedPaymentMethod]);

    useEffect(() => {
        if (!requiresPayMeshCustomerFields) {
            return;
        }
        setPaymentDetails(prev => ({
            ...prev,
            customer_first_name: prev.customer_first_name || profile.firstName || '',
            customer_last_name: prev.customer_last_name || profile.lastName || '',
            customer_email: prev.customer_email || profile.email || '',
            customer_address: prev.customer_address || profile.address || '',
            customer_city: prev.customer_city || profile.city || '',
            customer_country: prev.customer_country || profile.country || '',
            customer_zip_code: prev.customer_zip_code || profile.postalCode || '',
        }));
    }, [profile, requiresPayMeshCustomerFields]);

    const stablecoinToFiatMap: Record<string, string> = {
        USDT: 'USD',
        USDC: 'USD',
        EURC: 'EUR',
        EURT: 'EUR',
    };

    const manualConfigs = selectedPaymentMethod?.manual_currency_configs || [];
    const relevantManualConfigs = useMemo(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod)) return [];
        const accountCurrency = String(selectedAccount?.base_currency || '').toUpperCase();
        if ((selectedPaymentMethod?.method_category || 'fiat') === 'crypto') {
            return manualConfigs.filter(c => {
                const symbol = String(c.currency?.symbol || '').toUpperCase();
                return stablecoinToFiatMap[symbol] === accountCurrency;
            });
        }
        return manualConfigs.filter(
            c => String(c.currency?.symbol || '').toUpperCase() === accountCurrency
        );
    }, [manualConfigs, selectedPaymentMethod, selectedAccount?.base_currency]);

    const selectedManualConfig = useMemo(() => {
        if (!manualConfigId) return undefined;
        return relevantManualConfigs.find(c => c.id === manualConfigId);
    }, [relevantManualConfigs, manualConfigId]);

    useEffect(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod)) return;
        if (relevantManualConfigs.length === 1) {
            setManualConfigId(relevantManualConfigs[0].id);
            return;
        }
        if (manualConfigId && !relevantManualConfigs.some(c => c.id === manualConfigId)) {
            setManualConfigId('');
        }
    }, [selectedPaymentMethod?.id, relevantManualConfigs, manualConfigId]);

    useEffect(() => {
        if (!isManualPaymentMethod(selectedPaymentMethod) || !manualConfigId) {
            setManualLoading(false);
            return;
        }
        setManualLoading(true);
        const tmr = window.setTimeout(() => setManualLoading(false), 900);
        return () => window.clearTimeout(tmr);
    }, [manualConfigId, selectedPaymentMethod?.id]);

    const needsManualCurrency = isManualPaymentMethod(selectedPaymentMethod);
    const manualStepComplete =
        !needsManualCurrency || (!!manualConfigId && !manualLoading && !!selectedManualConfig);

    const showManualInstructions =
        needsManualCurrency && manualConfigId && !manualLoading && !!selectedManualConfig;

    const manualDetails = selectedManualConfig?.details || {};

    const getCurrencySymbol = (currency: string) => {
        const symbols: Record<string, string> = {
            USD: '$',
            EUR: '€',
            GBP: '£',
            JPY: '¥',
        };
        return symbols[currency] || currency;
    };

    const getAccountColor = (currency: string) => {
        const colors: Record<string, string> = {
            USD: 'bg-emerald-600',
            EUR: 'bg-indigo-600',
            GBP: 'bg-purple-600',
            JPY: 'bg-rose-600',
        };
        return colors[currency] || 'bg-slate-600';
    };

    const handleSubmit = async () => {
        if (!amount || !selectedMethod || !selectedAccountId) {
            enqueueSnackbar(t('pleaseFillAllFields'), { variant: 'error' });
            return;
        }

        const amountNum = parseFloat(amount);
        if (isNaN(amountNum) || amountNum <= 0) {
            enqueueSnackbar(t('pleaseEnterValidAmount'), { variant: 'error' });
            return;
        }

        if (
            !isDeposit &&
            selectedPaymentMethod?.requires_address &&
            !paymentDetails.address.trim()
        ) {
            enqueueSnackbar(t('pleaseEnterAddress'), { variant: 'error' });
            return;
        }

        if (
            !isDeposit &&
            selectedPaymentMethod?.requires_holder_name &&
            !paymentDetails.holder_name.trim()
        ) {
            enqueueSnackbar(t('pleaseEnterHolderName'), { variant: 'error' });
            return;
        }

        if (
            !isDeposit &&
            selectedPaymentMethod?.requires_bank_details &&
            !paymentDetails.iban.trim()
        ) {
            enqueueSnackbar(t('pleaseEnterIban'), { variant: 'error' });
            return;
        }

        if (!isDeposit && selectedPaymentMethod?.requires_card_details) {
            if (!paymentDetails.card_number.trim()) {
                enqueueSnackbar(t('pleaseEnterCardNumber'), { variant: 'error' });
                return;
            }
            if (!paymentDetails.expiry_date.trim()) {
                enqueueSnackbar(t('pleaseEnterExpiryDate'), { variant: 'error' });
                return;
            }
            if (!paymentDetails.cvv.trim()) {
                enqueueSnackbar(t('pleaseEnterCvv'), { variant: 'error' });
                return;
            }
        }

        const missingPayMeshFields = requiresPayMeshCustomerFields
            ? (
                  [
                      'customer_first_name',
                      'customer_last_name',
                      'customer_email',
                      'customer_dob',
                      'customer_address',
                      'customer_city',
                      'customer_country',
                      'customer_zip_code',
                  ] as const
              ).filter(key => !String(paymentDetails[key] || '').trim())
            : [];
        if (missingPayMeshFields.length > 0) {
            enqueueSnackbar(
                `Please complete required PayMesh details: ${missingPayMeshFields
                    .map(key => paymeshFieldLabels[key])
                    .join(', ')}`,
                { variant: 'error' }
            );
            return;
        }

        if (isManualPaymentMethod(selectedPaymentMethod) && !manualConfigId) {
            enqueueSnackbar(t('pleaseSelectCurrency') || 'Please select a currency', {
                variant: 'error',
            });
            return;
        }

        if (!isDeposit && selectedAccount && amountNum > Number(selectedAccount.balance)) {
            enqueueSnackbar(t('insufficientBalance') || 'Insufficient balance', {
                variant: 'error',
            });
            return;
        }

        try {
            // Prepare payment details if required
            const requestPaymentDetails =
                !isDeposit &&
                (selectedPaymentMethod?.requires_address ||
                    selectedPaymentMethod?.requires_card_details ||
                    selectedPaymentMethod?.requires_bank_details)
                    ? {
                          ...paymentDetails,
                          holder_name: paymentDetails.holder_name,
                          card_holder: paymentDetails.card_holder || paymentDetails.holder_name,
                          account_holder: paymentDetails.holder_name,
                          iban: paymentDetails.iban || undefined,
                      }
                    : requiresPayMeshCustomerFields
                      ? {
                            customer_first_name: paymentDetails.customer_first_name,
                            customer_last_name: paymentDetails.customer_last_name,
                            customer_email: paymentDetails.customer_email,
                            customer_dob: paymentDetails.customer_dob,
                            customer_address: paymentDetails.customer_address,
                            customer_city: paymentDetails.customer_city,
                            customer_country: paymentDetails.customer_country,
                            customer_zip_code: paymentDetails.customer_zip_code,
                        }
                      : undefined;

            const manual = isManualPaymentMethod(selectedPaymentMethod);

            let response;
            if (isDeposit) {
                response = await createDeposit.mutateAsync({
                    trading_account_id: selectedAccountId,
                    payment_method_id: selectedMethod,
                    amount: amountNum,
                    deposit_currency_id: manual ? selectedManualConfig?.currency_id : undefined,
                    deposit_manual_config_id: manual ? manualConfigId : undefined,
                    payment_details: requestPaymentDetails,
                });
            } else {
                response = await createWithdrawal.mutateAsync({
                    trading_account_id: selectedAccountId,
                    payment_method_id: selectedMethod,
                    amount: amountNum,
                    withdrawal_currency_id: manual ? selectedManualConfig?.currency_id : undefined,
                    withdrawal_manual_config_id: manual ? manualConfigId : undefined,
                    payment_details: requestPaymentDetails,
                });
            }

            // Check if response includes processor information
            if (response?.payment_url || response?.widget_url || response?.form_data) {
                setProcessorResponse(response);

                // Auto-redirect for PGPay form submission
                if (response.form_data && response.payment_url && response.method === 'POST') {
                    setTimeout(() => {
                        submitPGPayForm(response.payment_url, response.form_data);
                    }, 1000);
                }
            } else {
                // No processor, transaction created successfully
                enqueueSnackbar(
                    t('requestSubmittedSuccess', {
                        action: isDeposit ? t('depositAction') : t('withdrawalAction'),
                    }),
                    { variant: 'success' }
                );
                navigate('/accounts');
            }
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                (isDeposit ? t('failedToDepositFunds') : t('failedToWithdrawFunds'));
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    };

    const submitPGPayForm = (url: string, formData: Record<string, any>) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        form.target = '_blank';

        Object.entries(formData).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value);
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const handleRedirectToPayment = () => {
        if (processorResponse?.payment_url) {
            window.open(processorResponse.payment_url, '_blank');
        }
    };

    const handleOpenWidget = () => {
        if (processorResponse?.widget_url) {
            // For mobile, open in same window or use in-app browser if available
            window.location.href = processorResponse.widget_url;
        }
    };

    if (isLoadingAccounts) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-32">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    if (!selectedAccount) {
        return (
            <div className="min-h-screen text-foreground pb-32">
                <div className="px-4 pt-6">
                    <p className="text-muted-foreground">{t('noAccountsAvailable')}</p>
                </div>
                <MobileFloatingNav />
            </div>
        );
    }

    const currencySymbol = getCurrencySymbol(selectedAccount.base_currency);
    const accountColor = getAccountColor(selectedAccount.base_currency);

    // Show processor redirect UI if response received
    if (
        processorResponse &&
        (processorResponse.payment_url ||
            processorResponse.widget_url ||
            processorResponse.form_data)
    ) {
        const processorName = processorResponse.processor?.toUpperCase() || 'PAYMENT PROCESSOR';
        const isPGPay = processorResponse.processor === 'pgpay';
        const isTransak = processorResponse.processor === 'transak';
        const isPayMesh = processorResponse.processor === 'paymesh';

        return (
            <div className="min-h-screen text-foreground pb-32">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                    <button
                        onClick={() => {
                            setProcessorResponse(null);
                            setAmount('');
                            setSelectedMethod('');
                            setManualConfigId('');
                            setPaymentDetails({
                                address: '',
                                holder_name: '',
                                iban: '',
                                card_number: '',
                                card_holder: '',
                                expiry_date: '',
                                cvv: '',
                                customer_first_name: '',
                                customer_last_name: '',
                                customer_email: '',
                                customer_dob: '',
                                customer_address: '',
                                customer_city: '',
                                customer_country: '',
                                customer_zip_code: '',
                            });
                        }}
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <span className="font-semibold text-lg">{t('paymentProcessing')}</span>
                    <div className="w-6 h-6" />
                </div>

                <div className="px-4 pt-6 space-y-6">
                    {/* Processor Info Card */}
                    <div className="bg-gradient-to-br from-slate-900 to-blue-900/20 rounded-xl p-6 border border-blue-800/30">
                        <div className="flex items-center gap-3 mb-4">
                            {isPGPay ? (
                                <Zap className="w-8 h-8 text-primary" />
                            ) : isTransak ? (
                                <Shield className="w-8 h-8 text-primary" />
                            ) : isPayMesh ? (
                                <CreditCard className="w-8 h-8 text-primary" />
                            ) : (
                                <CheckCircle2 className="w-8 h-8 text-primary" />
                            )}
                            <div>
                                <h2 className="text-xl font-bold">
                                    {t('redirectingTo', { processor: processorName })}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {t('completePaymentOnPage', {
                                        action: isDeposit
                                            ? t('depositAction')
                                            : t('withdrawalAction'),
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {processorResponse.form_data && processorResponse.method === 'POST' ? (
                        <div className="space-y-4">
                            <div className="trader-glass rounded-2xl p-4">
                                <p className="text-sm text-muted-foreground mb-2">
                                    {t('pgpayWindowOpens')}
                                </p>
                            </div>
                        </div>
                    ) : processorResponse.widget_url ? (
                        <div className="space-y-4">
                            <div className="trader-glass rounded-2xl overflow-hidden">
                                <iframe
                                    src={processorResponse.widget_url}
                                    className="w-full h-[600px] border-0"
                                    title="Transak Payment Widget"
                                    allow="camera; microphone; payment"
                                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                                />
                            </div>
                            <button
                                onClick={handleOpenWidget}
                                className="w-full py-3 trader-glass hover:bg-foreground/10 rounded-xl font-semibold text-base transition-colors flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                {t('openInNewWindow')}
                            </button>
                        </div>
                    ) : processorResponse.payment_url ? (
                        <div className="space-y-4">
                            <button
                                onClick={handleRedirectToPayment}
                                className="w-full py-4 trader-fab-gradient text-white hover:opacity-90 rounded-xl font-semibold text-lg transition-opacity flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-5 h-5" />
                                {isDeposit
                                    ? 'Click Here to Continue Your Deposit'
                                    : t('openInNewWindow')}
                            </button>
                        </div>
                    ) : null}

                    {processorResponse.transaction && (
                        <div className="trader-glass rounded-2xl p-4">
                            <p className="text-xs text-muted-foreground mb-1">
                                {t('transactionId')}
                            </p>
                            <p className="text-sm font-mono text-muted-foreground">
                                {processorResponse.transaction.id}
                            </p>
                        </div>
                    )}
                </div>

                <MobileFloatingNav />
            </div>
        );
    }

    return (
        <div className="min-h-screen text-foreground pb-32">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                <button onClick={() => navigate('/accounts')}>
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <span className="font-semibold text-lg">
                    {isDeposit ? t('depositFunds') : t('withdrawFunds')}
                </span>
                <Lock className="w-6 h-6 text-muted-foreground" />
            </div>

            <div className="px-4 pt-6">
                {/* Select Account */}
                <div className="mb-6 account-dropdown">
                    <label className="text-sm text-muted-foreground mb-2 block">
                        {isDeposit ? t('toAccount') : t('fromAccount')}
                    </label>
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowAccountDropdown(!showAccountDropdown);
                                setShowPaymentMethodDropdown(false);
                            }}
                            className="w-full trader-glass rounded-2xl p-4 flex items-center justify-between hover:bg-foreground/5 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className={`w-10 h-10 ${accountColor} rounded-lg flex items-center justify-center text-xl font-bold`}
                                >
                                    {currencySymbol}
                                </div>
                                <div>
                                    <div className="font-semibold text-left">
                                        {selectedAccount.base_currency} {t('tradingAccount')}
                                    </div>
                                    <div className="text-xs text-muted-foreground text-left">
                                        {t('acc')}:{' '}
                                        {selectedAccount.account_number || selectedAccount.id}
                                    </div>
                                </div>
                            </div>
                            <ChevronDown
                                className={`w-5 h-5 text-muted-foreground transition-transform ${
                                    showAccountDropdown ? 'rotate-180' : ''
                                }`}
                            />
                        </button>

                        {/* Account Dropdown */}
                        {showAccountDropdown && (
                            <div className="absolute z-10 w-full mt-2 trader-glass-strong rounded-2xl overflow-hidden shadow-lg">
                                {tradingAccounts?.accounts?.map(account => {
                                    const accColor = getAccountColor(account.base_currency);
                                    const accSymbol = getCurrencySymbol(account.base_currency);
                                    const isSelected = account.id === selectedAccountId;
                                    return (
                                        <button
                                            key={account.id}
                                            onClick={() => {
                                                setSelectedAccountId(account.id);
                                                setShowAccountDropdown(false);
                                                setSelectedMethod(''); // Reset payment method when account changes
                                                setManualConfigId('');
                                                // Reset payment details when account changes
                                                setPaymentDetails({
                                                    address: '',
                                                    holder_name: '',
                                                    iban: '',
                                                    card_number: '',
                                                    card_holder: '',
                                                    expiry_date: '',
                                                    cvv: '',
                                                    customer_first_name: '',
                                                    customer_last_name: '',
                                                    customer_email: '',
                                                    customer_dob: '',
                                                    customer_address: '',
                                                    customer_city: '',
                                                    customer_country: '',
                                                    customer_zip_code: '',
                                                });
                                            }}
                                            className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-foreground/10 transition-colors ${
                                                isSelected ? 'bg-primary/15' : ''
                                            }`}
                                        >
                                            <div
                                                className={`w-10 h-10 ${accColor} rounded-lg flex items-center justify-center text-xl font-bold`}
                                            >
                                                {accSymbol}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-semibold">
                                                    {account.base_currency} {t('tradingAccount')}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {t('acc')}:{' '}
                                                    {account.account_number || account.id}
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <div className="w-2 h-2 rounded-full bg-primary"></div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Account Balance */}
                <div className="trader-glass rounded-2xl p-6 mb-6">
                    <div className="text-sm text-muted-foreground mb-2">
                        {t('availableBalance')}
                    </div>
                    <div className="text-3xl font-bold mb-1">
                        {formatCurrencyAmount(
                            selectedAccount.balance,
                            selectedAccount.base_currency
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {t('currency')}: {selectedAccount.base_currency}
                    </div>
                </div>

                {/* Amount + fee (before payment method — matches desktop manual flow) */}
                <div className="mb-6">
                    <label className="text-sm text-muted-foreground mb-2 block">
                        {isDeposit ? t('depositAmount') : t('withdrawalAmount')}
                    </label>
                    <div className="trader-glass rounded-2xl overflow-hidden">
                        <div className="flex items-center px-4 py-4">
                            <span className="text-2xl text-muted-foreground mr-2">
                                {currencySymbol}
                            </span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="flex-1 bg-transparent outline-none text-foreground text-2xl placeholder:text-muted-foreground rounded px-2"
                            />
                        </div>
                    </div>
                </div>

                {amount && (
                    <div className="trader-glass rounded-2xl p-4 mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-muted-foreground">
                                {isDeposit ? t('depositAmount') : t('withdrawalAmount')}
                            </span>
                            <span className="text-sm font-medium">
                                {currencySymbol}
                                {amount}
                            </span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-muted-foreground">
                                {t('processingFee')}
                            </span>
                            <span className="text-sm font-medium">{currencySymbol}0.00</span>
                        </div>
                        <div className="border-t border-border pt-2 mt-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-semibold">
                                    {isDeposit ? t('youWillDeposit') : t('youWillReceive')}
                                </span>
                                <span className="text-lg font-bold text-trader-positive">
                                    {currencySymbol}
                                    {amount}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payment Method */}
                <div className="mb-6 payment-dropdown">
                    <label className="text-sm text-muted-foreground mb-2 block">
                        {isDeposit ? t('depositMethod') : t('withdrawalMethod')}
                    </label>
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowPaymentMethodDropdown(!showPaymentMethodDropdown);
                                setShowAccountDropdown(false);
                            }}
                            className="w-full trader-glass rounded-2xl p-4 flex items-center justify-between hover:bg-foreground/5 transition-colors"
                        >
                            <span className="text-muted-foreground">
                                {selectedMethod
                                    ? availablePaymentMethods.find(pm => pm.id === selectedMethod)
                                          ?.display_name
                                    : isDeposit
                                      ? t('selectDepositMethod')
                                      : t('selectWithdrawalMethod')}
                            </span>
                            <ChevronDown
                                className={`w-5 h-5 text-muted-foreground transition-transform ${
                                    showPaymentMethodDropdown ? 'rotate-180' : ''
                                }`}
                            />
                        </button>

                        {/* Payment Method Dropdown */}
                        {showPaymentMethodDropdown && availablePaymentMethods.length > 0 && (
                            <div className="absolute z-10 w-full mt-2 trader-glass-strong rounded-2xl overflow-hidden shadow-lg">
                                {availablePaymentMethods.map(method => {
                                    const isSelected = method.id === selectedMethod;
                                    return (
                                        <button
                                            key={method.id}
                                            onClick={() => {
                                                setSelectedMethod(method.id);
                                                setShowPaymentMethodDropdown(false);
                                                setManualConfigId('');
                                                // Reset payment details when method changes
                                                setPaymentDetails({
                                                    address: '',
                                                    holder_name: '',
                                                    iban: '',
                                                    card_number: '',
                                                    card_holder: '',
                                                    expiry_date: '',
                                                    cvv: '',
                                                });
                                            }}
                                            className={`w-full px-4 py-3 text-left hover:bg-foreground/10 transition-colors ${
                                                isSelected
                                                    ? 'trader-fab-gradient text-white'
                                                    : 'text-muted-foreground'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span>{method.display_name}</span>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {isManualPaymentMethod(method) ? (
                                                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-foreground/10">
                                                            Manual
                                                        </span>
                                                    ) : method.supports_processors ? (
                                                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-foreground/10">
                                                            Processor
                                                        </span>
                                                    ) : null}
                                                    {isSelected && (
                                                        <div className="w-2 h-2 rounded-full bg-white"></div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {showPaymentMethodDropdown &&
                            availablePaymentMethods.length === 0 &&
                            selectedAccount && (
                                <div className="absolute z-10 w-full mt-2 trader-glass-strong rounded-2xl p-4 shadow-lg">
                                    <p className="text-muted-foreground text-sm">
                                        {t('noPaymentMethodsForCurrency', {
                                            currency: selectedAccount.base_currency,
                                        })}
                                    </p>
                                </div>
                            )}
                    </div>
                </div>

                {/* Payment Method Note */}
                {selectedPaymentMethod?.note && (
                    <div className="mb-6 trader-glass rounded-xl p-4">
                        <p className="text-sm text-foreground">{selectedPaymentMethod.note}</p>
                    </div>
                )}

                {isManualPaymentMethod(selectedPaymentMethod) &&
                    relevantManualConfigs.length > 0 && (
                        <div className="mb-6 space-y-3">
                            <label className="text-sm text-muted-foreground mb-2 block">
                                {isDeposit
                                    ? t('depositCurrencyInstructions') ||
                                      'Deposit currency (instructions)'
                                    : t('withdrawalCurrencyInstructions') ||
                                      'Payout currency (instructions)'}
                            </label>
                            {relevantManualConfigs.length > 1 && (
                                <div className="space-y-2">
                                    {relevantManualConfigs.map((c, idx) => (
                                        <button
                                            key={c.id || `${c.currency_id}-${idx}`}
                                            type="button"
                                            onClick={() => setManualConfigId(c.id || '')}
                                            className={`w-full rounded-xl border p-3 text-left text-sm ${
                                                manualConfigId === c.id
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-border bg-foreground/5'
                                            }`}
                                        >
                                            {(c.currency?.symbol || c.currency_id) +
                                                (c.detail_type === 'crypto' && c.details?.network
                                                    ? ` - ${c.details.network}`
                                                    : c.detail_type === 'bank' &&
                                                        c.details?.bank_name
                                                      ? ` - ${c.details.bank_name}`
                                                      : ` - ${c.detail_type}`)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {manualConfigId && manualLoading && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {isDeposit
                                        ? t('loadingInstructions') ||
                                          'Retrieving payment instructions…'
                                        : t('loadingPayoutInstructions') ||
                                          'Retrieving payout instructions…'}
                                </div>
                            )}
                            {showManualInstructions && selectedManualConfig && (
                                <div className="trader-glass rounded-2xl p-4 space-y-3 text-sm">
                                    <p className="text-muted-foreground font-medium">
                                        {selectedManualConfig.currency?.symbol}
                                    </p>
                                    {selectedManualConfig.detail_type === 'bank' && (
                                        <div className="space-y-1 text-foreground">
                                            {manualDetails.bank_name && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        Bank:{' '}
                                                    </span>
                                                    {manualDetails.bank_name}
                                                </p>
                                            )}
                                            {manualDetails.iban && (
                                                <p className="break-all">
                                                    <span className="text-muted-foreground">
                                                        IBAN:{' '}
                                                    </span>
                                                    {manualDetails.iban}
                                                </p>
                                            )}
                                            {manualDetails.account_number && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        Account:{' '}
                                                    </span>
                                                    {manualDetails.account_number}
                                                </p>
                                            )}
                                            {manualDetails.swift && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        SWIFT:{' '}
                                                    </span>
                                                    {manualDetails.swift}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {selectedManualConfig.detail_type === 'crypto' && (
                                        <div className="space-y-3">
                                            {manualDetails.network && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        Network:{' '}
                                                    </span>
                                                    {manualDetails.network}
                                                </p>
                                            )}
                                            {manualDetails.wallet_address && (
                                                <>
                                                    <div className="flex justify-center rounded-lg border border-border bg-white p-3 w-fit mx-auto">
                                                        <QRCode
                                                            value={String(
                                                                manualDetails.wallet_address
                                                            )}
                                                            size={140}
                                                            level="M"
                                                        />
                                                    </div>
                                                    <p className="break-all font-mono text-xs text-foreground">
                                                        {manualDetails.wallet_address}
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {selectedManualConfig.detail_type === 'credit_card' && (
                                        <div className="space-y-1 text-foreground">
                                            {manualDetails.card_holder && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        Card holder:{' '}
                                                    </span>
                                                    {manualDetails.card_holder}
                                                </p>
                                            )}
                                            {manualDetails.card_number && (
                                                <p>
                                                    <span className="text-muted-foreground">
                                                        Card number:{' '}
                                                    </span>
                                                    {manualDetails.card_number}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {selectedManualConfig.detail_type === 'custom' && (
                                        <p className="whitespace-pre-wrap text-foreground">
                                            {manualDetails.instructions || manualDetails.text}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                {/* Address Field */}
                {!isDeposit && selectedPaymentMethod?.requires_address && (
                    <div className="mb-6">
                        <label className="text-sm text-muted-foreground mb-2 block">
                            {isDeposit ? t('address') : t('withdrawalAddress')}
                        </label>
                        <input
                            type="text"
                            value={paymentDetails.address}
                            onChange={e =>
                                setPaymentDetails(prev => ({ ...prev, address: e.target.value }))
                            }
                            placeholder={
                                isDeposit ? t('enterAddress') : t('enterWithdrawalAddress')
                            }
                            className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                            required
                        />
                    </div>
                )}

                {/* Holder Name Field */}
                {!isDeposit && selectedPaymentMethod?.requires_holder_name && (
                    <div className="mb-6">
                        <label className="text-sm text-muted-foreground mb-2 block">
                            {t('accountCardHolderName')}
                        </label>
                        <input
                            type="text"
                            value={paymentDetails.holder_name}
                            onChange={e =>
                                setPaymentDetails(prev => ({
                                    ...prev,
                                    holder_name: e.target.value,
                                }))
                            }
                            placeholder={t('enterFullName')}
                            className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                            required
                        />
                    </div>
                )}

                {/* Bank Details (IBAN) */}
                {!isDeposit && selectedPaymentMethod?.requires_bank_details && (
                    <div className="mb-6">
                        <label className="text-sm text-muted-foreground mb-2 block">
                            {t('iban')}
                        </label>
                        <input
                            type="text"
                            value={paymentDetails.iban}
                            onChange={e =>
                                setPaymentDetails(prev => ({ ...prev, iban: e.target.value }))
                            }
                            placeholder={t('enterIban')}
                            className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                            required
                        />
                    </div>
                )}

                {/* Card Details */}
                {!isDeposit && selectedPaymentMethod?.requires_card_details && (
                    <div className="mb-6 space-y-4">
                        <div>
                            <label className="text-sm text-muted-foreground mb-2 block">
                                {t('cardNumber')}
                            </label>
                            <input
                                type="text"
                                value={paymentDetails.card_number}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        card_number: e.target.value,
                                    }))
                                }
                                placeholder={t('enterCardNumber')}
                                className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    {t('expiryDate')}
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.expiry_date}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            expiry_date: e.target.value,
                                        }))
                                    }
                                    placeholder="MM/YY"
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    CVV
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.cvv}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            cvv: e.target.value,
                                        }))
                                    }
                                    placeholder="CVV"
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                )}

                {isDeposit && requiresPayMeshCustomerFields && (
                    <div className="mb-6 space-y-4">
                        <div className="text-sm text-muted-foreground">
                            PayMesh required details
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.customer_first_name}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            customer_first_name: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.customer_last_name}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            customer_last_name: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-muted-foreground mb-2 block">
                                Email
                            </label>
                            <input
                                type="email"
                                value={paymentDetails.customer_email}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_email: e.target.value,
                                    }))
                                }
                                className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-sm text-muted-foreground mb-2 block">
                                Date of Birth
                            </label>
                            <input
                                type="date"
                                value={paymentDetails.customer_dob}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_dob: e.target.value,
                                    }))
                                }
                                className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-sm text-muted-foreground mb-2 block">
                                Address
                            </label>
                            <input
                                type="text"
                                value={paymentDetails.customer_address}
                                onChange={e =>
                                    setPaymentDetails(prev => ({
                                        ...prev,
                                        customer_address: e.target.value,
                                    }))
                                }
                                className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    City
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.customer_city}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            customer_city: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    Country
                                </label>
                                <input
                                    type="text"
                                    placeholder="US"
                                    value={paymentDetails.customer_country}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            customer_country: e.target.value.toUpperCase(),
                                        }))
                                    }
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    ZIP Code
                                </label>
                                <input
                                    type="text"
                                    value={paymentDetails.customer_zip_code}
                                    onChange={e =>
                                        setPaymentDetails(prev => ({
                                            ...prev,
                                            customer_zip_code: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-background/50 rounded-xl p-4 border border-border text-foreground"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Continue Button */}
                <button
                    onClick={handleSubmit}
                    disabled={
                        !amount ||
                        !selectedMethod ||
                        !manualStepComplete ||
                        createDeposit.isPending ||
                        createWithdrawal.isPending
                    }
                    className="w-full py-4 trader-fab-gradient text-white hover:opacity-90 rounded-xl font-semibold text-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {createDeposit.isPending || createWithdrawal.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                        t('continue')
                    )}
                </button>

                {/* Info Text */}
                <div className="mt-6 space-y-2 text-xs text-muted-foreground">
                    <p>
                        • {isDeposit ? t('minimumDeposit') : t('minimumWithdrawal')}:{' '}
                        {currencySymbol}50.00
                    </p>
                    <p>• {t('bankTransfersDays')}</p>
                    <p>
                        • {t('cardDays', { action: isDeposit ? t('deposits') : t('withdrawals') })}
                    </p>
                    <p>• {t('eWalletHours')}</p>
                    <p>
                        •{' '}
                        {t('subjectToVerification', {
                            action: isDeposit ? t('deposits') : t('withdrawals'),
                        })}
                    </p>
                </div>
            </div>

            <MobileFloatingNav />
        </div>
    );
};

export default MobileDepositWithdrawPage;
